import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Award, FileText, Receipt } from 'lucide-react';
import SelectableCard from '../../../components/SelectableCard';
import StatusBadge from '../../../components/StatusBadge';
import Switch from '../../../components/Switch';
import MappingList from '../../../components/MappingList';
import LoyaltyProgramsTab from './LoyaltyProgramsTab';
import {
	listPaymentTermMappings,
	addPaymentTermMapping,
	removePaymentTermMapping,
	listStorePaymentMethods,
	addStorePaymentMethod,
	removeStorePaymentMethod,
	listStoreLoyaltyPrograms,
	listPaceTerms,
} from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

const PAYMENT_COLUMNS = [
	{ key: 'paymentTermsName', header: 'Payment Terms Name' },
	{ key: 'erpTermsId', header: 'ERP Terms ID' },
];

// Pace's Terms.id is the actual FK sent when creating a Job/Estimate --
// letting the admin type it by hand risked a typo'd ID that only surfaces as
// an ERP submission failure much later, at checkout. Pulling the live list
// from Pace (same pace-lookup endpoint the store-creation wizard already
// uses -- see paceOnboarding.js) guarantees the saved ID is one Pace
// actually recognizes.
const loadPaceTermsOptions = async () => {
	const result = await listPaceTerms();
	if (!result?.success) return [];
	return (result.data || []).map((term) => ({
		value: String(term.id),
		label: term.description || `Terms ${term.id}`,
	}));
};

const PAYMENT_ADD_FIELDS = [
	{
		key: 'erpTermsId',
		label: 'ERP Terms ID',
		required: true,
		type: 'select',
		loadOptions: loadPaceTermsOptions,
		onSelect: (option) => ({ paymentTermsName: option?.label ?? '' }),
	},
	{ key: 'paymentTermsName', label: 'Payment Terms Name', required: true },
];

function EnabledBadge({ enabled }) {
	return <StatusBadge status={enabled ? 'active' : 'inactive'} text={enabled ? 'Enabled' : 'Disabled'} />;
}

function CreditCardPanel({ storeId, row, onChange, canEdit, disabledReason }) {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const toggle = async (nextChecked) => {
		setSaving(true);
		setError('');
		const result = nextChecked
			? await addStorePaymentMethod(storeId, 'CREDIT_CARD')
			: await removeStorePaymentMethod(row.storePaymentMethodId);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to update.');
			return;
		}
		onChange();
	};

	return (
		<div className='space-y-3'>
			<Switch
				checked={row != null}
				disabled={saving || !canEdit || (!row && !!disabledReason)}
				onChange={toggle}
				label='Accept credit card payments'
			/>
			{!row && disabledReason && <p className='text-sm text-gray-500'>{disabledReason}</p>}
			{error && <p className='text-sm text-pi-red'>{error}</p>}
			<p className='text-sm text-gray-500 border border-dashed border-gray-300 rounded-md px-3 py-2'>
				Coming soon: Authorize.net account connection details will be configured here.
			</p>
		</div>
	);
}

function PurchaseOrderPanel({ storeId, row, onChange, canEdit, disabledReason }) {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const toggle = async (nextChecked) => {
		setSaving(true);
		setError('');
		const result = nextChecked
			? await addStorePaymentMethod(storeId, 'PURCHASE_ORDER')
			: await removeStorePaymentMethod(row.storePaymentMethodId);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to update.');
			return;
		}
		onChange();
	};

	return (
		<div className='space-y-3'>
			<Switch
				checked={row != null}
				disabled={saving || !canEdit || (!row && !!disabledReason)}
				onChange={toggle}
				label='Accept purchase order (PO) payments'
			/>
			{!row && disabledReason && <p className='text-sm text-gray-500'>{disabledReason}</p>}
			{error && <p className='text-sm text-pi-red'>{error}</p>}
			<p className='text-sm text-gray-500 border border-dashed border-gray-300 rounded-md px-3 py-2'>
				Customers provide a PO number at checkout instead of paying up front. Matches ERP's "PO Required" checkout option.
			</p>
		</div>
	);
}

export default function PaymentMethodsTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [expanded, setExpanded] = useState({
		CREDIT_CARD: false,
		PURCHASE_ORDER: false,
		LOYALTY_POINTS: false,
		INVOICE: false,
	});
	const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

	const [paymentMethodRows, setPaymentMethodRows] = useState([]);
	const refreshPaymentMethods = useCallback(
		() => listStorePaymentMethods(storeId).then((result) => {
			if (result?.success) setPaymentMethodRows(result.data);
		}),
		[storeId],
	);
	useEffect(() => {
		refreshPaymentMethods();
	}, [refreshPaymentMethods]);
	const creditCardRow = paymentMethodRows.find((m) => m.paymentMethod === 'CREDIT_CARD');
	const purchaseOrderRow = paymentMethodRows.find((m) => m.paymentMethod === 'PURCHASE_ORDER');

	const [loyaltyPrograms, setLoyaltyPrograms] = useState([]);
	useEffect(() => {
		listStoreLoyaltyPrograms(storeId).then((result) => {
			if (result?.success) setLoyaltyPrograms(result.data);
		});
	}, [storeId]);

	const listPayment = useCallback(() => listPaymentTermMappings(storeId), [storeId]);
	const addPayment = useCallback((fields) => addPaymentTermMapping(storeId, fields), [storeId]);
	const [paymentTermMappings, setPaymentTermMappings] = useState([]);
	useEffect(() => {
		listPaymentTermMappings(storeId).then((result) => {
			if (result?.success) setPaymentTermMappings(result.data);
		});
	}, [storeId]);
	const handlePaymentTermMappingsChange = useCallback((result) => {
		if (result?.success) setPaymentTermMappings(result.data);
	}, []);

	// Loyalty Points and every other payment method are mutually exclusive for
	// a store (enforced server-side too, in createStoreLoyaltyProgram /
	// createStorePaymentMethod / createPaymentTermMapping) -- disable
	// activating whichever side isn't already on, rather than letting a
	// request round-trip just to bounce off the server's 409.
	const loyaltyEnabled = loyaltyPrograms.length > 0;
	const otherMethodsActive = Boolean(creditCardRow) || Boolean(purchaseOrderRow) || paymentTermMappings.length > 0;
	const disabledByLoyalty = loyaltyEnabled ? 'Disable Loyalty Points to enable this.' : null;
	const disabledByOtherMethods = otherMethodsActive
		? 'Disable Credit Card, Purchase Order, and Invoice to enable Loyalty Points.'
		: null;

	return (
		<div className='space-y-4'>
			<h2 className='text-base font-medium mb-2'>Accepted Payment Methods</h2>

			<SelectableCard
				icon={CreditCard}
				title='Credit Card'
				description='Charge orders to a credit card via Authorize.net.'
				badge={<EnabledBadge enabled={Boolean(creditCardRow)} />}
				expanded={expanded.CREDIT_CARD}
				onToggle={() => toggleExpanded('CREDIT_CARD')}
			>
				<CreditCardPanel
					storeId={storeId}
					row={creditCardRow}
					onChange={refreshPaymentMethods}
					canEdit={canEdit}
					disabledReason={disabledByLoyalty}
				/>
			</SelectableCard>

			<SelectableCard
				icon={Receipt}
				title='Purchase Order'
				description='Bill orders to a customer-provided PO number instead of collecting payment up front.'
				badge={<EnabledBadge enabled={Boolean(purchaseOrderRow)} />}
				expanded={expanded.PURCHASE_ORDER}
				onToggle={() => toggleExpanded('PURCHASE_ORDER')}
			>
				<PurchaseOrderPanel
					storeId={storeId}
					row={purchaseOrderRow}
					onChange={refreshPaymentMethods}
					canEdit={canEdit}
					disabledReason={disabledByLoyalty}
				/>
			</SelectableCard>

			<SelectableCard
				icon={FileText}
				title='Invoice'
				description='Bill the order on ERP payment terms instead of collecting payment up front.'
				badge={<EnabledBadge enabled={paymentTermMappings.length > 0} />}
				expanded={expanded.INVOICE}
				onToggle={() => toggleExpanded('INVOICE')}
			>
				{disabledByLoyalty && <p className='text-sm text-gray-500 mb-3'>{disabledByLoyalty}</p>}
				<MappingList
					list={listPayment}
					add={addPayment}
					remove={removePaymentTermMapping}
					columns={PAYMENT_COLUMNS}
					addFields={PAYMENT_ADD_FIELDS}
					idKey='paymentTermMappingId'
					emptyHeading='No payment term mappings yet'
					collapsibleAdd
					onChange={handlePaymentTermMappingsChange}
					canEdit={canEdit && !disabledByLoyalty}
				/>
			</SelectableCard>

			<SelectableCard
				icon={Award}
				title='Loyalty Points'
				description='Let members redeem orders using loyalty program points.'
				badge={<EnabledBadge enabled={loyaltyPrograms.length > 0} />}
				expanded={expanded.LOYALTY_POINTS}
				onToggle={() => toggleExpanded('LOYALTY_POINTS')}
			>
				{disabledByOtherMethods && <p className='text-sm text-gray-500 mb-3'>{disabledByOtherMethods}</p>}
				<LoyaltyProgramsTab storeId={storeId} onChange={setLoyaltyPrograms} canEdit={canEdit && !disabledByOtherMethods} />
			</SelectableCard>			
		</div>
	);
}
